import {
    TransactionColumn,
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { View } from "@common/types/views/views";
import { calculateSplitSiblings } from "@helpers/transactionSplitUtils";
import {
    DependencyList,
    memo,
    useCallback,
    useContext,
    useDeferredValue,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { TransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { useNonLockedList } from "@hooks/useNonLockedList";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { useViewTableLoadMore } from "@hooks/useLoadMoreTableState";
import { useTransactionViewConfigState } from "@components/transactions/views/useTransactionViewConfig";
import { useFocusedTransactionCellShortcuts } from "@components/transactions/views/useFocusedTransactionCellShortcuts";
import { ViewConfig } from "@lib/views";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useOverlays } from "../../../contexts/OverlayContext";
import { TransactionSortValue } from "../useSort";
import {
    ViewTable,
    ViewTableHandle,
    type ViewTableRowDragOut,
} from "@design-system/ViewTable/ViewTable";
import { NoTransactions } from "../NoTransactions";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../PaginatedTransactionsTable.context";
import { useBulkActions } from "../TransactionsBulkActions/useBulkActions";
import styles from "./TransactionsView.module.scss";
import {
    TransactionsViewFooter,
    TransactionsViewFooterProps,
} from "./TransactionsViewFooter";

const EMPTY_ROW_SELECTION: RowSelectionState = {};
const getCellActionKey = (columnId: string, rowId: string | number) =>
    `${columnId}:${rowId}`;

interface Props {
    view: TransactionView;
    setSelectedTransactionId?: (id: number | string | undefined) => void;
    onOpenTransactionReview?: (id: number) => void;
    selectedTransactionId?: number;
    emptyView?: () => React.ReactNode;
    autoSelect?: boolean;
    pinnedRows?: TransactionListItem[];
    prependContent?: React.ReactNode;
    appendContent?: React.ReactNode;
    fixedTopOffsetPx?: number | true;
    recomputeTopOffsetOn?: DependencyList;
    footer?: (options?: TransactionsViewFooterProps) => React.ReactNode;
    customTableMeta?: TransactionsTableMeta;
    selectAllOnOpen?: boolean;
    actionComponent?: () => React.ReactNode;
    footerClassName?: string;
    // Allows callers like SimilarTransactionsUpdateDrawer to inject a custom
    // column config (e.g. with renamed headers or extra columns).
    viewConfigOverride?: ViewConfig<
        TransactionListItem,
        string,
        TransactionSortValue
    >;
    className?: string;
    onViewChange?: (view: View) => void;
    transformViewChange?: (view: TransactionView) => TransactionView;
    // Lets the page trigger a cell's action UI imperatively (e.g. reopen the
    // schedule picker on a row after the create-schedule drawer closes).
    openCellActionRef?: React.MutableRefObject<
        TransactionsTableMeta["openCellAction"]
    >;
    // Reports the keyboard or click focused row so the page can act on it.
    onFocusedTransactionChange?: (transactionId: number | null) => void;
    isEnterSelectionDisabled?: boolean;
    // For pages that bind `m` to their own memo action.
    isNoteShortcutDisabled?: boolean;
    rowDragOut?: ViewTableRowDragOut<TransactionListItem>;
}

const renderNoTransactions = () => <NoTransactions />;

export const TransactionsView: React.FC<Props> = memo(
    ({
        view,
        setSelectedTransactionId,
        onOpenTransactionReview,
        selectedTransactionId,
        emptyView = renderNoTransactions,
        autoSelect = false,
        pinnedRows,
        prependContent,
        appendContent,
        fixedTopOffsetPx,
        recomputeTopOffsetOn,
        footer,
        customTableMeta,
        selectAllOnOpen,
        actionComponent,
        footerClassName: _footerClassName,
        viewConfigOverride,
        className,
        onViewChange: onViewChangeProp,
        transformViewChange,
        openCellActionRef,
        onFocusedTransactionChange,
        isEnterSelectionDisabled,
        isNoteShortcutDisabled,
        rowDragOut,
    }) => {
        const {
            transactions: immediateTransactions,
            currentSort,
            currentPage,
            totalPages,
            disableSimilarTransactionsUpdate,
            isTransactionsFetching,
            loadMore,
        } = useContext(PaginatedTransactionsDataContext);
        const { getOverlayInfo } = useOverlays();
        // While the similar-transactions update drawer is open, the page's
        // outer TransactionsView stays mounted underneath. Both the inner
        // (drawer) and outer (page) views register the same focused-cell and
        // pagination hotkeys on `c`, `m`, `e`, `g`, and `mod+arrow`. The inner
        // view sets `disableSimilarTransactionsUpdate=true`, so we use that
        // marker to keep its hotkeys live while silencing the outer view's so
        // pressing the keys doesn't open a phantom popover behind the drawer.
        const outerViewShortcutsDisabled =
            !!getOverlayInfo("similarTransactionsUpdate") &&
            !disableSimilarTransactionsUpdate;
        // Defer transactions so the expensive table row rendering yields to
        // higher-priority updates (e.g. filter UI toggling a radio button).
        // When cached data returns synchronously, this prevents the 100+ row
        // re-render from blocking the main thread during the urgent frame.
        const transactions = useDeferredValue(immediateTransactions);
        const { handleSortChange, setPage, setCurrentView } = useContext(
            PaginatedTransactionsActionsContext,
        );
        const data = useMemo(
            () =>
                transactions && pinnedRows && currentPage === 1
                    ? [...pinnedRows, ...transactions]
                    : (transactions ?? null),
            [transactions, pinnedRows, currentPage],
        );
        const {
            hasSelectedAll,
            selectedCount,
            select,
            clearSelection,
            selectAll,
            selected,
            enabled,
        } = useBulkActions();

        const nonLockedTransactions = useNonLockedList(transactions);

        const viewTableRef = useRef<ViewTableHandle>(null);

        // Stable scrollTo wrapper that lazily reads the ref — safe to capture
        // in memoized footer without going stale when the ref is assigned later.
        const scrollTo = useCallback((options: ScrollToOptions) => {
            viewTableRef.current?.scrollTo(options);
        }, []);

        // Page navigation callbacks
        const handleNextPage = useCallback(() => {
            if (currentPage < totalPages) {
                setPage(currentPage + 1);
            }
        }, [currentPage, totalPages, setPage]);

        const handlePreviousPage = useCallback(() => {
            if (currentPage > 1) {
                setPage(currentPage - 1);
            }
        }, [currentPage, setPage]);

        const isPageShortcutEnabled = !outerViewShortcutsDisabled && !loadMore;
        useKeyboardCommand({
            keys: "mod+ArrowRight",
            callback: handleNextPage,
            options: {
                enabled: isPageShortcutEnabled,
                description: "Go to next page",
            },
        });
        useKeyboardCommand({
            keys: "mod+ArrowLeft",
            callback: handlePreviousPage,
            options: {
                enabled: isPageShortcutEnabled,
                description: "Go to previous page",
            },
        });

        const tableLoadMore = useViewTableLoadMore(
            loadMore,
            "Failed to load more transactions",
        );

        const enableRowSelection = useMemo(() => {
            if (!view.options?.bulkActions) {
                return false;
            }
            // Disable individual row selection when "select all" is active
            if (hasSelectedAll) {
                return false;
            }
            return true;
        }, [view.options?.bulkActions, hasSelectedAll]);

        useEffect(() => {
            if (
                view.options?.selection &&
                autoSelect &&
                data &&
                data.length > 0 &&
                !selectedTransactionId
            ) {
                setSelectedTransactionId?.(data[0].id);
            }
        }, [
            autoSelect,
            data,
            selectedTransactionId,
            setSelectedTransactionId,
            view.options?.selection,
        ]);

        const automaticallySelectedAllRef = useRef(false);

        useEffect(() => {
            if (
                !automaticallySelectedAllRef.current &&
                selectAllOnOpen &&
                !hasSelectedAll &&
                selectedCount === 0 &&
                data
            ) {
                automaticallySelectedAllRef.current = true;
                if (data.length < 100) {
                    select(data);
                } else {
                    selectAll();
                }
            }
        }, [
            selectAllOnOpen,
            data,
            hasSelectedAll,
            selectedCount,
            select,
            selectAll,
        ]);

        const rowSelection = useMemo(() => {
            if (!enabled) {
                return EMPTY_ROW_SELECTION;
            }

            const selection: RowSelectionState = {};

            if (hasSelectedAll) {
                nonLockedTransactions?.forEach((transaction) => {
                    selection[transaction.id] = true;
                });
            } else {
                Object.keys(selected).forEach((id) => {
                    selection[id] = true;
                });
            }

            return Object.keys(selection).length === 0
                ? EMPTY_ROW_SELECTION
                : selection;
        }, [selected, enabled, hasSelectedAll, nonLockedTransactions]);

        const selectedTransactionIdRef = useRef(selectedTransactionId);
        selectedTransactionIdRef.current = selectedTransactionId;
        const cellActionOpenersRef = useRef<Map<string, () => void>>(new Map());

        const registerCellActionOpener = useCallback(
            ({
                columnId,
                rowId,
                open,
            }: {
                columnId: string;
                rowId: string | number;
                open: () => void;
            }) => {
                const key = getCellActionKey(columnId, rowId);
                cellActionOpenersRef.current.set(key, open);
                return () => {
                    cellActionOpenersRef.current.delete(key);
                };
            },
            [],
        );

        const openCellAction = useCallback(
            ({
                columnId,
                rowId,
            }: {
                columnId: string;
                rowId: string | number;
            }) => {
                const key = getCellActionKey(columnId, rowId);
                const open = cellActionOpenersRef.current.get(key);
                if (!open) {
                    return false;
                }
                open();
                return true;
            },
            [],
        );

        useImperativeHandle(openCellActionRef, () => openCellAction, [
            openCellAction,
        ]);

        const handleRowClick = useCallback(
            (row?: TransactionListItem) => {
                // If we hit escape and there's nothing selected, clear bulk selection
                if (
                    selectedTransactionIdRef.current === undefined &&
                    row === undefined
                ) {
                    clearSelection();
                }

                const clickedRowId = row?.id;
                if (
                    clickedRowId !== undefined &&
                    selectedTransactionIdRef.current === clickedRowId
                ) {
                    setSelectedTransactionId?.(undefined);
                    return;
                }

                setSelectedTransactionId?.(clickedRowId);
            },
            [setSelectedTransactionId, clearSelection],
        );

        const dataRef = useRef(data);
        dataRef.current = data;
        const scrolledToSelectedIdRef = useRef<number | null>(null);

        useEffect(() => {
            if (selectedTransactionId == null) {
                scrolledToSelectedIdRef.current = null;
                return;
            }
            if (scrolledToSelectedIdRef.current === selectedTransactionId) {
                return;
            }
            const isOnCurrentPage = dataRef.current?.some(
                (transaction) => transaction.id === selectedTransactionId,
            );
            if (!isOnCurrentPage) {
                return;
            }
            scrolledToSelectedIdRef.current = selectedTransactionId;
            viewTableRef.current?.scrollToRow(String(selectedTransactionId), {
                align: "center",
            });
        }, [selectedTransactionId, data]);

        const { handleFocusedRowChange: handleFocusedRowCellShortcuts } =
            useFocusedTransactionCellShortcuts({
                selectedCount,
                dataRef,
                openCellAction,
                enabled: !outerViewShortcutsDisabled,
                isNoteShortcutDisabled,
            });

        const handleFocusedRowChange = useCallback(
            (
                index: number | null,
                row: TransactionListItem | null,
                source: "keyboard" | "click",
            ) => {
                handleFocusedRowCellShortcuts(index, row);
                onFocusedTransactionChange?.(row?.id ?? null);
                if (
                    source === "keyboard" &&
                    selectedTransactionId !== undefined &&
                    row
                ) {
                    handleRowClick(row);
                }
            },
            [
                handleFocusedRowCellShortcuts,
                onFocusedTransactionChange,
                selectedTransactionId,
                handleRowClick,
            ],
        );

        // Hoist data hooks to table level to avoid per-row useQuery subscriptions
        const financialAccounts = useFinancialAccounts();
        const categoriesById = useCategoriesById();
        const hasScheduleColumn = view.columns.some(
            (column) => column.id === TransactionColumn.SCHEDULE,
        );
        const scheduleTemplatesQuery = useScheduleTemplates({
            enabled: hasScheduleColumn,
        });
        const tableMeta = useMemo(
            () => ({
                ...customTableMeta,
                financialAccounts,
                categoriesById,
                scheduleTemplates: scheduleTemplatesQuery.data,
                registerCellActionOpener,
                openCellAction,
                callbacks: {
                    // Uses dataRef (not data) intentionally: data changes on every
                    // fetch, which would defeat tableMeta memoization. The ref
                    // always holds the latest data array at call time.
                    onOpenTransactionReview,
                    calculateSplitSiblings: (
                        transaction: TransactionListItem,
                    ) => {
                        const currentData = dataRef.current;
                        const index = currentData?.findIndex(
                            (t) => t.id === transaction.id,
                        );
                        if (index === undefined || !currentData) {
                            return {
                                hasTopSibling: false,
                                hasBottomSibling: false,
                            };
                        }
                        return calculateSplitSiblings(
                            transaction,
                            index,
                            currentData,
                        );
                    },
                    ...customTableMeta?.callbacks,
                },
            }),
            [
                customTableMeta,
                financialAccounts,
                categoriesById,
                scheduleTemplatesQuery.data,
                registerCellActionOpener,
                openCellAction,
                onOpenTransactionReview,
            ],
        );

        // Memoize the bulk actions methods to prevent unnecessary re-renders
        const memoizedBulkActions = useMemo(
            () => ({
                enabled,
                clearSelection,
                select,
            }),
            [enabled, clearSelection, select],
        );

        const rowSelectionRef = useRef(rowSelection);
        rowSelectionRef.current = rowSelection;

        const handleRowSelectionChange = useCallback<
            OnChangeFn<RowSelectionState>
        >(
            (updater) => {
                if (!memoizedBulkActions.enabled || !dataRef.current) {
                    return;
                }

                const nextSelection =
                    typeof updater === "function"
                        ? updater(rowSelectionRef.current)
                        : updater;

                const selectedIds = Object.keys(nextSelection).filter(
                    (id) => nextSelection[id],
                );

                const selectedTransactions = dataRef.current.filter(
                    (transaction) =>
                        selectedIds.includes(String(transaction.id)),
                );

                if (selectedIds.length === 0) {
                    memoizedBulkActions.clearSelection();
                } else {
                    memoizedBulkActions.clearSelection();
                    memoizedBulkActions.select(selectedTransactions);
                }
            },
            [memoizedBulkActions],
        );

        const handleViewChange = useCallback(
            (updatedView: View) => {
                const parsedView = TransactionViewSchema.safeParse({
                    ...view,
                    columns: updatedView.columns,
                });
                if (!parsedView.success) {
                    Sentry.captureException(parsedView.error);
                    return;
                }
                const viewToPersist = transformViewChange
                    ? transformViewChange(parsedView.data)
                    : parsedView.data;
                setCurrentView((prev) => ({
                    ...prev,
                    columns: viewToPersist.columns,
                }));
                onViewChangeProp?.(viewToPersist);
            },
            [onViewChangeProp, setCurrentView, transformViewChange, view],
        );

        const memoizedFooter = useMemo(
            () =>
                footer ? (
                    footer({ scrollTo })
                ) : (
                    <TransactionsViewFooter
                        scrollTo={scrollTo}
                        actionComponent={actionComponent}
                    />
                ),
            [footer, actionComponent, scrollTo],
        );

        const { transactionsViewConfig: defaultViewConfig } =
            useTransactionViewConfigState();
        const transactionsViewConfig = viewConfigOverride ?? defaultViewConfig;

        // Stabilize view reference: ViewTable only reads columns/options/type,
        // not filters or other transient properties. Without this, every filter
        // change creates a new view object that defeats React.memo on ViewTable.
        // Intentionally excludes view.filters, view.sort, etc. from deps.
        const viewForTable = useMemoByKeys(
            () => view,
            [view.columns, view.options, view.type],
        );

        return (
            <>
                <ViewTable
                    ref={viewTableRef}
                    fixedTopOffsetPx={fixedTopOffsetPx}
                    recomputeTopOffsetOn={recomputeTopOffsetOn}
                    className={classNames(
                        styles.transactionsViewContainer,
                        styles.transactionsView,
                        className,
                    )}
                    data={data}
                    view={viewForTable}
                    config={transactionsViewConfig}
                    selectedRowId={selectedTransactionId}
                    onFocusedRowChange={handleFocusedRowChange}
                    onRowClick={handleRowClick}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    enableRowSelection={enableRowSelection}
                    enableKeyboardShortcuts
                    isEnterSelectionDisabled={isEnterSelectionDisabled}
                    sort={currentSort}
                    onSortChange={handleSortChange}
                    isSortLoading={isTransactionsFetching}
                    emptyView={emptyView}
                    prependContent={prependContent}
                    appendContent={appendContent}
                    tableMeta={tableMeta}
                    onViewChange={handleViewChange}
                    enableColumnResizing
                    footer={memoizedFooter}
                    rowDragOut={rowDragOut}
                    loadMore={tableLoadMore}
                />
            </>
        );
    },
);
TransactionsView.displayName = "TransactionsView";
